<template>
    <div id="chatbot-archive">
        <div class="d-flex">
            <div class="archives-panels">
                <ConversationList
                    :items="items"
                    :active-chat-id="activeChatId"
                    :on-conversation-selected="onConversationSelected"
                    :filters="filters"
                    @on-filters-changed="onFiltersChanged($event)"
                    :loading="loading"
                    :show-load-more="items.length < total"
                    :loadMore="loadMore"
                    :getAllVisitorId="getAllVisitorId"
                    :getConversationById="getConversationById"
                />
                <ConversationViewer :conversation="getSelectedConversation()" :skeleton-loading="loading" />
                <ConversationDetail :conversation="getSelectedConversation()" :loading="loading" />
            </div>
        </div>
    </div>
</template>

<script>
import "./chatbot-archive.scss";
import ConversationList from "./ConversationList";
import ConversationViewer from "./ConversationViewer";
import ConversationDetail from "./ConversationDetail";
import log2 from "../../../../../utils/log2";
import { CHATBOT_API } from "../../../../../api/chatbotv2";
import _ from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import SideMenuBar from "../../SideMenuBar";
import moment from "moment/moment";
import { PERMISSION_TYPES } from "../../../../../language/permissionTypes";
import ChatbotMenu from "../../ChatbotMenu.vue";

const log = log2("ChatbotArchive");
const defaultStartDate = moment().startOf("day").toISOString();
const defaultEndDate = moment().endOf("day").toISOString();
export default {
    name: "ChatbotArchive",
    components: {
        ChatbotMenu,
        ConversationList,
        ConversationViewer,
        ConversationDetail,
        SideMenuBar,
    },
    data() {
        return {
            period: null,
            loading: true,
            items: [],
            total: 0,
            activeChatId: null,
            clickCount: 0,
            concatMode: false,
            pagination: {
                skipCount: 0,
                limit: 20,
            },
            filters: {
                period: this.$route.query.period || "last7",
                startDate: this.$route.query.allTime
                    ? null
                    : this.$route.query.startDate
                    ? moment(parseInt(this.$route.query.startDate)).toISOString()
                    : defaultStartDate,
                endDate: this.$route.query.allTime
                    ? null
                    : this.$route.query.endDate
                    ? moment(parseInt(this.$route.query.endDate)).toISOString()
                    : defaultEndDate,
                integration: this.$route.query.integration || null,
                storyId: this.$route.query.storyId || null,
                visitorId: this.$route.query.visitorId || null,
                conversationId: this.$route.query.conversationId || null,
            },
        };
    },
    computed: {
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        ...mapState({
            stories: (state) => state.CB.stories,
        }),
    },
    methods: {
        ...mapActions({
            listStories: "CB/listStories",
        }),
        onFiltersChanged(e) {
            this.pagination = { skipCount: 0, limit: 20 };
            this.filters = { ...e };
            log("on filter change", e);
            this.clickCount = 0;
        },
        fetchDelay: _.debounce(function () {
            this.fetch();
        }, 500),
        loadMore() {
            if (!this.loading) {
                this.clickCount++;
                this.concatMode = true;
                this.pagination = {
                    ...this.pagination,
                    skipCount: !!this.items ? this.items.length : 0,
                    limit: !this.items.length
                        ? 20
                        : Math.floor(10 * Math.pow(1.25, this.clickCount)) > 100
                        ? 100
                        : Math.floor(10 * Math.pow(1.25, this.clickCount)),
                };
            }
        },
        fetch() {
            this.loading = true;
            const filters = {};
            filters["period"] = _.get(this.filters, "period");
            filters["integration"] = _.get(this.filters, "integration");
            filters["storyId"] = _.get(this.filters, "storyId");
            filters["visitorId"] = _.get(this.filters, "visitorId");
            filters["conversationId"] = _.get(this.filters, "conversationId");
            filters["startDate"] = _.get(this.filters, "startDate");
            filters["endDate"] = _.get(this.filters, "endDate");
            this.updateQueryParams("period", filters["period"]);
            this.updateQueryParams("integration", filters["integration"]);
            this.updateQueryParams("storyId", filters["storyId"]);
            this.updateQueryParams("visitorId", filters["visitorId"]);
            this.updateQueryParams("conversationId", filters["conversationId"]);
            this.updateQueryParams("startDate", filters["startDate"]);
            this.updateQueryParams("endDate", filters["endDate"]);

            CHATBOT_API.ARCHIVE.list({ ...filters, ...this.pagination })
                .then(async (res) => {
                    log("fetch", res.data);
                    const { items, total, pageNumber, limit } = res.data;
                    this.items = this.concatMode ? this.items.concat(items) : items;
                    this.items = _.uniqBy(this.items, (x) => x._id);
                    this.total = total;
                    if (!this.activeChatId) {
                        this.activeChatId = _.get(items, "[0]._id", null);
                    }
                })
                .catch((err) => {
                    log("fetch", err);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        updateQueryParams(key, value) {
            if (value === "" || value == null) {
                let query = Object.assign({}, this.$route.query);
                delete query[key];
                this.$router.replace({ query });
            } else {
                let { query } = this.$route;
                this.$router.replace({
                    query: { ...query, [key]: encodeURI(value) },
                });
            }
        },
        onConversationSelected(id) {
            this.activeChatId = id;
        },
        getSelectedConversation() {
            return this.items.find((item) => item._id === this.activeChatId);
        },
        getAllVisitorId() {
            return this.items.map((x) => _.get(x, "visitor._id", "")) || [];
        },
        getConversationById() {
            return this.items.map((x) => _.get(x, "_id", "")) || [];
        },
    },
    mounted() {
        this.listStories();
        this.fetch();
    },
    beforeMount() {
        if (this.checkPermission(PERMISSION_TYPES["chatbot.archive.view"].tr.name) === false) {
            return router.push("/denied");
        }
    },
    watch: {
        filters: {
            handler(newValue, oldValue) {
                log("filters changed", this.filters);
                if (newValue.conversationId !== oldValue.conversationId) this.activeChatId = newValue.conversationId;
                this.concatMode = false;
                this.fetchDelay();
            },
            deep: true,
        },
        pagination: {
            handler() {
                log("pagination changed", this.pagination);
                this.fetchDelay();
            },
            deep: true,
        },
    },
};
</script>
